import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { dashboardService } from "../../firebase/services";
import {
  Users,
  UserCheck,
  UserX,
  FileCheck,
  ArrowRight,
  ShieldAlert,
  Loader2,
  CalendarCheck,
  FileText,
  Calendar,
  Bell,
  Award,
  RefreshCw,
} from "lucide-react";

export const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalRegistrations: 0,
    pendingApplications: 0,
    approvedStudents: 0,
    rejectedApplications: 0,
  });
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await dashboardService.getAdminStats();
      setStats(data);
    } catch (err) {
      console.error("Admin dashboard stats error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admin Control Panel</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Kian Technologies, Bhilai • 15 Days Free Cyber Security Masterclass Operations
          </p>
        </div>
        <button
          onClick={fetchStats}
          data-testid="admin-refresh-stats-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card hover:bg-muted text-xs font-mono transition-colors self-start sm:self-auto"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          <span>Refresh Real Stats</span>
        </button>
      </div>

      {/* Real Firestore Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Registrations */}
        <div className="p-5 rounded-xl bg-card border border-border space-y-2 shadow-sm">
          <div className="flex items-center justify-between text-muted-foreground text-xs font-mono">
            <span>Total Applications</span>
            <FileCheck className="w-4 h-4 text-cyan-400" />
          </div>
          <div
            data-testid="admin-stat-total"
            className="text-3xl font-extrabold text-foreground font-mono"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-cyan-400" /> : stats.totalRegistrations}
          </div>
          <p className="text-[11px] text-muted-foreground font-mono">
            All submitted application docs
          </p>
        </div>

        {/* Pending Review */}
        <div className="p-5 rounded-xl bg-card border border-amber-500/30 bg-amber-500/5 space-y-2 shadow-sm">
          <div className="flex items-center justify-between text-amber-300 text-xs font-mono">
            <span>Pending Review</span>
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse"></span>
          </div>
          <div
            data-testid="admin-stat-pending"
            className="text-3xl font-extrabold text-amber-400 font-mono"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-amber-400" /> : stats.pendingApplications}
          </div>
          <Link
            to="/admin/registrations?filter=pending"
            className="text-[11px] font-mono text-amber-400 hover:underline inline-flex items-center gap-1"
          >
            <span>Review pending applicants</span>
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Approved Students */}
        <div className="p-5 rounded-xl bg-card border border-emerald-500/30 bg-emerald-500/5 space-y-2 shadow-sm">
          <div className="flex items-center justify-between text-emerald-400 text-xs font-mono">
            <span>Approved Students</span>
            <UserCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div
            data-testid="admin-stat-approved"
            className="text-3xl font-extrabold text-emerald-400 font-mono"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-emerald-400" /> : stats.approvedStudents}
          </div>
          <Link
            to="/admin/students"
            className="text-[11px] font-mono text-emerald-400 hover:underline inline-flex items-center gap-1"
          >
            <span>View student directory</span>
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Rejected */}
        <div className="p-5 rounded-xl bg-card border border-rose-500/30 bg-rose-500/5 space-y-2 shadow-sm">
          <div className="flex items-center justify-between text-rose-400 text-xs font-mono">
            <span>Rejected Applications</span>
            <UserX className="w-4 h-4 text-rose-400" />
          </div>
          <div
            data-testid="admin-stat-rejected"
            className="text-3xl font-extrabold text-rose-400 font-mono"
          >
            {loading ? <Loader2 className="w-6 h-6 animate-spin text-rose-400" /> : stats.rejectedApplications}
          </div>
          <p className="text-[11px] text-muted-foreground font-mono">
            Unverified or duplicate records
          </p>
        </div>
      </div>

      {/* Quick Admin Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Verification Hub */}
        <div className="p-6 rounded-xl bg-card border border-border space-y-4">
          <div className="flex items-center space-x-2">
            <UserCheck className="w-5 h-5 text-emerald-400" />
            <h2 className="font-bold text-base">Applicant Verification & Approval</h2>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Verify submitted college credentials, cyber security interests, and passport photos. Approving an applicant generates their unique Student ID (e.g. <code className="font-mono text-emerald-400">KIAN-CYBER-XXXX</code>). The applicant then activates their own portal account.
          </p>
          <div className="pt-2">
            <Link
              to="/admin/registrations"
              data-testid="admin-go-registrations-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-mono text-xs font-medium transition-colors"
            >
              <span>Open Registrations Table</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Class Roll Call & Resources */}
        <div className="p-6 rounded-xl bg-card border border-border space-y-4">
          <div className="flex items-center space-x-2">
            <CalendarCheck className="w-5 h-5 text-cyan-400" />
            <h2 className="font-bold text-base">Daily Attendance & Study Resources</h2>
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Record daily present/absent roll-calls for activated students and attach HTTPS links to lab sheets, slides, and tools for each of the 15 masterclass days.
          </p>
          <div className="pt-2 flex flex-wrap gap-2">
            <Link
              to="/admin/attendance"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card hover:bg-muted text-xs font-mono font-medium transition-colors"
            >
              <CalendarCheck className="w-3.5 h-3.5 text-cyan-400" />
              <span>Mark Attendance</span>
            </Link>
            <Link
              to="/admin/materials"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border bg-card hover:bg-muted text-xs font-mono font-medium transition-colors"
            >
              <FileText className="w-3.5 h-3.5 text-emerald-400" />
              <span>Upload Materials</span>
            </Link>
            <Link
              to="/admin/certificates"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/10 text-xs font-mono font-medium transition-colors"
            >
              <Award className="w-3.5 h-3.5 text-emerald-400" />
              <span>Manage Certificates</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Module Shortcuts */}
      <div className="p-5 rounded-xl bg-card border border-border space-y-4">
        <h3 className="text-xs font-mono font-semibold uppercase text-muted-foreground tracking-wider">
          Masterclass Management Modules
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Link
            to="/admin/registrations"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-1 transition-colors"
          >
            <UserCheck className="w-4 h-4 mx-auto text-emerald-400" />
            <span className="block text-xs font-mono font-medium">Registrations</span>
          </Link>
          <Link
            to="/admin/students"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-1 transition-colors"
          >
            <Users className="w-4 h-4 mx-auto text-cyan-400" />
            <span className="block text-xs font-mono font-medium">Students</span>
          </Link>
          <Link
            to="/admin/schedule"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-1 transition-colors"
          >
            <Calendar className="w-4 h-4 mx-auto text-emerald-400" />
            <span className="block text-xs font-mono font-medium">15D Schedule</span>
          </Link>
          <Link
            to="/admin/announcements"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-1 transition-colors"
          >
            <Bell className="w-4 h-4 mx-auto text-cyan-400" />
            <span className="block text-xs font-mono font-medium">Announcements</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
